import { DateTime } from 'luxon';

import { hockeyFormatter } from '@nhl/formatter-utilities';

/*  Season Selector
    The server supplies us with a JS object representing the following about seasons:
    - seasonId
    - enhancedStatsAvailable
    - gameDecidedIn (whether its OT or not)
    - gameResults
    - playerGameByGameAvailable
    - endDate
    - regularSeasonEndDate
    - startDate
    - teamGameByGame available
    - teams

    This information is used for the following:
    - providing the options in the season select
    - validating information/selected dropdown options for season and report options
*/

// Even though this doesn't use `this`, other modules use it
const getSeasonFromDate = (date) => {
    return hockeyFormatter.getApproximateSeasonByDate(DateTime.fromISO(date));
};

const getDataForSeason = (statsConfig, seasonId) => {
    return statsConfig.seasonData[seasonId];
};

const getSeasonsForRange = (statsConfig, range) => {
    if (!range) {
        return statsConfig.seasonData;
    }

    const { from, to } = range;
    return (
        Object.keys(statsConfig.seasonData)
            // Mapping all with number just in case they come as strings
            .map((seasonId) => Number(seasonId))
            .filter((seasonId) => {
                // Using multiple returns here since it's simpler than having an `isValid` flag and modifying it
                if (from && to) {
                    return seasonId >= from && seasonId <= to;
                }
                if (from) {
                    return seasonId >= from;
                }
                if (to) {
                    return seasonId <= to;
                }
                return seasonId;
            })
            // Bring our list back as an object
            .reduce((map, seasonId) => ({ ...map, [seasonId]: statsConfig.seasonData[seasonId] }), {})
    );
};

const getSeasonsForDateRange = (statsConfig, range) => {
    const { from, to } = range;
    const seasonFromID = getSeasonFromDate(from);
    const seasonToID = getSeasonFromDate(to);
    const seasonRange = { from: seasonFromID, to: seasonToID };

    return getSeasonsForRange(statsConfig, seasonRange);
};

const getSeasonsForSelectedRange = (statsConfig, report) => {
    const { timeFrame, isDateRangeFromSeasons, seasonFrom, seasonTo, dateFrom, dateTo } = report;

    const range =
        timeFrame === 'game' && !isDateRangeFromSeasons
            ? { from: dateFrom, to: dateTo }
            : { from: seasonFrom, to: seasonTo };
    return timeFrame === 'game' && !isDateRangeFromSeasons
        ? getSeasonsForDateRange(statsConfig, range)
        : getSeasonsForRange(statsConfig, range);
};

const getSeasonsAsDropdownOptions = (statsConfig, range) => {
    const seasonsMapForRange = getSeasonsForRange(statsConfig, range);
    return Object.keys(seasonsMapForRange).map((seasonId) => ({
        value: Number(seasonId),
        label: hockeyFormatter.singleSeasonFormatter(seasonId),
        key: seasonId,
    }));
};

const getListFromSeasonMap = (statsConfig, seasonsMapForRange, thingToMakeListOf) => {
    const thingsForRange = Object.keys(seasonsMapForRange)
        .map((seasonId) =>
            statsConfig.seasonData[seasonId] ? statsConfig.seasonData[seasonId][thingToMakeListOf] : [],
        )
        .reduce((arr, teamArr) => arr.concat(teamArr), []);
    return Array.from(new Set(thingsForRange));
};

/**
 * For a particular time frame (Game or Season)
 * Compute the different items for a particluar field (thingToMakeListOf)
 * @param {*} report
 * @param {*} thingToMakeListOf
 */
const getListOfThingsForReport = (statsConfig, report, thingToMakeListOf) => {
    const seasonsMap = getSeasonsForSelectedRange(statsConfig, report);
    return getListFromSeasonMap(statsConfig, seasonsMap, thingToMakeListOf);
};

const isPlayoffsInSelectedRange = (statsConfig, report) => {
    const { seasonId } = statsConfig.fieldDefaultData;

    const seasonsMap = getSeasonsForSelectedRange(statsConfig, report);
    const [seasonFrom] = Object.keys(seasonsMap).sort();

    // If seasonFrom is the current season, check if today is after the end of the regular season
    return parseInt(seasonFrom) >= seasonId
        ? DateTime.local() > DateTime.fromISO(seasonsMap[seasonFrom].regularSeasonEndDate)
        : true;
};

export default {
    getDataForSeason,
    getSeasonFromDate,
    getSeasonsForRange,
    getSeasonsForDateRange,
    getSeasonsAsDropdownOptions,
    getListFromSeasonMap,
    getSeasonsForSelectedRange,
    getListOfThingsForReport,
    isPlayoffsInSelectedRange,
};
